import { useCallback } from 'react';
import { useBulkImport } from '@/hooks/useBulkImport';

export default function BulkUpload() {
  const {
    file,
    mappedRows,
    columnMapping,
    availableColumns,
    loading,
    progress,
    jobId,
    jobStatus,
    parseFile,
    setColumnMapping,
    validateRows,
    submitBatch,
    reset,
  } = useBulkImport();

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (selectedFile) {
        parseFile(selectedFile);
      }
    },
    [parseFile],
  );

  const handleValidate = useCallback(() => {
    validateRows();
  }, [validateRows]);

  const handleSubmit = useCallback(async () => {
    await submitBatch();
  }, [submitBatch]);

  const validCount = mappedRows.filter((r) => r.status === 'valid').length;
  const invalidCount = mappedRows.filter((r) => r.status === 'invalid').length;

  return (
    <div className="space-y-6">
      {!file ? (
        <div className="card">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-4 text-lg font-semibold text-foreground">Upload CSV File</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Upload a CSV file with your product data for bulk import
            </p>
            <div className="mt-4">
              <label className="btn-primary cursor-pointer">
                <input type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
                Select CSV File
              </label>
            </div>
            <p className="mt-2 text-2xs text-muted-foreground">
              Required columns: name, price, category, sku, stock, description
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-foreground">{file.name}</h3>
                <p className="text-sm text-muted-foreground">
                  {mappedRows.length > 0
                    ? `${validCount} valid, ${invalidCount} invalid`
                    : 'Click validate to check rows'}
                </p>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={handleValidate} className="btn-secondary">
                  Validate
                </button>
                {validCount > 0 && (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="btn-primary"
                  >
                    {loading ? 'Processing...' : `Import ${validCount} Products`}
                  </button>
                )}
                <button type="button" onClick={reset} className="btn-secondary">
                  Reset
                </button>
              </div>
            </div>
          </div>

          {availableColumns.length > 0 && (
            <div className="card">
              <h4 className="mb-4 text-sm font-semibold text-foreground">Column Mapping</h4>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {['name', 'price', 'category', 'sku', 'stock', 'description'].map((required) => (
                  <div key={required}>
                    <label className="label-field capitalize">{required}</label>
                    <select
                      value={columnMapping[required] || ''}
                      onChange={(e) =>
                        setColumnMapping({ ...columnMapping, [required]: e.target.value })
                      }
                      className="input-field mt-1"
                    >
                      <option value="">-- Select column --</option>
                      {availableColumns.map((col) => (
                        <option key={col} value={col}>
                          {col}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {mappedRows.length > 0 && (
            <div className="card">
              <h4 className="mb-4 text-sm font-semibold text-foreground">Row Preview</h4>
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-surface">
                    <tr className="border-b border-border">
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">#</th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">Name</th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">Price</th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">SKU</th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappedRows.slice(0, 50).map((row) => (
                      <tr key={row.rowIndex} className="border-b border-border">
                        <td className="px-3 py-2 text-muted-foreground">{row.rowIndex + 1}</td>
                        <td className="px-3 py-2">{row.data.name}</td>
                        <td className="px-3 py-2">SGD {row.data.price}</td>
                        <td className="px-3 py-2">{row.data.sku}</td>
                        <td className="px-3 py-2">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                              row.status === 'valid'
                                ? 'bg-success/10 text-success'
                                : 'bg-destructive/10 text-destructive'
                            }`}
                          >
                            {row.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {mappedRows.length > 50 && (
                  <p className="mt-2 text-center text-sm text-muted-foreground">
                    Showing 50 of {mappedRows.length} rows
                  </p>
                )}
              </div>
            </div>
          )}

          {jobId && (
            <div className="card">
              <h4 className="mb-4 text-sm font-semibold text-foreground">Import Progress</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground capitalize">{jobStatus}</span>
                  <span className="font-medium text-foreground">{progress}%</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary-600 transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
